import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLevel } from '../features/level/api/getLevel';
import { getLevels } from '../features/search/api/getLevels';
import SearchBox from '../components/SearchBox/SearchBox';
import DemonFace from '../components/shared/DemonFace';
import { DemonLogoSizes } from '../utils/difficultyToImgSrc';
import type { GetLevelsResponse } from '@gdladder/shared/features';

interface LevelSearchOptions {
    defaultLevel?: number | null;
    isInvalid?: boolean;
    onLevel: (level: GetLevelsResponse['data'][0] | undefined) => void;
}

export default function useLevelSearch(ID: string, { defaultLevel, isInvalid, onLevel }: LevelSearchOptions) {
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const { data, status } = useQuery({
        queryKey: ['levelSearch', searchQuery],
        queryFn: () => getLevels({ name: searchQuery, limit: 5, page: 0 }),
    });

    const { data: defaultData } = useQuery({
        queryKey: ['level', defaultLevel],
        queryFn: () => getLevel(defaultLevel!),
        enabled: defaultLevel != null,
    });

    const onChange = (value: string) => {
        setSearch(value);
        if (value !== search) onLevel(undefined);
    };

    useEffect(() => {
        if (!defaultData) return;

        onLevel({
            id: defaultData.ID,
            difficulty: defaultData.Meta.Difficulty,
            enjoyment: defaultData.Enjoyment,
            isComplete: 0,
            isInPack: 0,
            name: defaultData.Meta.Name,
            publisherName: defaultData.Meta.Publisher?.name ?? null,
            rarity: defaultData.Meta.Rarity,
            rating: defaultData.Rating,
            showcase: defaultData.Showcase,
            songName: defaultData.Meta.Song.Name,
        });
        // only set default level on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultData?.ID]);

    const renderResult = (level: GetLevelsResponse['data'][0]) => {
        const tier = level.rating !== null ? Math.round(level.rating) : null;

        return (
            <span className='flex min-w-0 items-center gap-3'>
                <DemonFace diff={level.difficulty} rarity={level.rarity} size={DemonLogoSizes.TINY} />
                <span className='min-w-0 grow'>
                    <span className='flex min-w-0 items-center gap-2'>
                        <span className='truncate font-bold'>{level.name}</span>
                        <span
                            className={`min-w-4.5 shrink-0 round:rounded px-1.25 py-0.5 text-center text-xs font-bold tier-${tier ?? 0}`}
                        >
                            {tier ?? '-'}
                        </span>
                    </span>
                    <span className='block truncate text-sm text-theme-300'>
                        by {level.publisherName ?? '-'}
                        {level.id > 3 && (
                            <>
                                <span className='mx-2 text-theme-400'>·</span>
                                ID {level.id}
                            </>
                        )}
                    </span>
                </span>
            </span>
        );
    };

    useEffect(() => {
        if (!defaultData) return;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSearch(defaultData.Meta.Name);
    }, [defaultData]);

    const clear = () => {
        setSearch('');
        setSearchQuery('');
        onLevel(undefined);
    };

    const handleResult = (level?: GetLevelsResponse['data'][0]) => {
        setSearch(level ? `${level.name} by ${level.publisherName ?? '-'}` : '-');
        onLevel(level);
    };

    return {
        clear,
        SearchBox: (
            <SearchBox<GetLevelsResponse['data'][0]>
                getLabel={(level) => `${level.name} by ${level.publisherName ?? '-'}`}
                getKey={(level) => level.id}
                renderResult={renderResult}
                value={search}
                onChange={onChange}
                onDebouncedChange={setSearchQuery}
                id={ID}
                list={data?.data ?? []}
                onResult={handleResult}
                status={status}
                invalid={isInvalid}
                placeholder={defaultData?.Meta.Name ?? 'Search for a level...'}
            />
        ),
        value: search,
    };
}
